import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LOGO_URL = "https://public.readdy.ai/ai/img_res/1465fc3d-9fcb-4ff8-a972-fea1715c1e21.png";

const productImages = [
  'https://public.readdy.ai/ai/img_res/6203e656-81f6-4e73-888e-80c3b1d5925f.png',
  'https://public.readdy.ai/ai/img_res/3b0cc5bb75bae9e858ac7d94b18547db.jpg',
  'https://readdy.ai/api/search-image?query=wooden%20euro%20pallet%20grade%20B%20inspection%20quality%20check%20warehouse%20floor%20Belgium&width=800&height=600&seq=gb4&orientation=landscape',
];

const pricingTiers = [
  { range: '10-100', price: 12, unit: '€/pallet' },
  { range: '100-500', price: 10, unit: '€/pallet' },
  { range: '500-5000', price: 9, unit: '€/pallet' },
  { range: '5000+', price: 8, unit: '€/pallet' },
];

const technicalSpecs = [
  { label: 'Dimensions', value: '1200 × 800 mm (EUR/EPAL)' },
  { label: 'Load Capacity', value: 'Up to 1000 kg dynamic' },
  { label: 'Condition', value: 'Good — minor cosmetic wear' },
  { label: 'Wood Type', value: 'Hardwood / Softwood' },
  { label: 'Repair Status', value: 'Minor repairs may be present' },
  { label: 'Certification', value: 'Quality inspected' },
  { label: 'Availability', value: 'In stock — large quantities' },
  { label: 'Min. Order Qty', value: 'Contact us for details' },
];

const keyFeatures = [
  'Functional & reliable',
  'Quality inspected',
  'Economical choice',
  'Good load capacity',
  'Minor cosmetic wear acceptable',
  'Structurally sound',
];

const qualityBadges = [
  { icon: 'ri-checkbox-circle-line', label: 'Inspected' },
  { icon: 'ri-price-tag-3-line', label: 'Best Value' },
  { icon: 'ri-store-3-line', label: 'In Stock' },
];

export default function GradeBPage() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  // Add SEO metadata dynamically
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    document.title = 'Grade B Used Euro Pallets Antwerp | Budget Pallets Belgium | PEL Partner';

    const setMeta = (selector: string, nameAttr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (el) { el.setAttribute('content', value); }
      else {
        el = document.createElement('meta');
        el.setAttribute(nameAttr, selector.match(/\[(?:name|property)="([^"]+)"\]/)?.[1] ?? '');
        el.setAttribute('content', value);
        document.head.appendChild(el);
      }
    };

    setMeta('meta[name="description"]', 'name', 'Cost-effective Grade B used Euro pallets in Antwerp, Belgium. Quality inspected, structurally sound, great value. Prices from €6/pallet. Fast delivery across Belgium. Contact +32 487 26 19 70.');
    setMeta('meta[name="keywords"]', 'name', 'Grade B pallets, budget pallets, value pallets, used pallets Antwerp, cheap euro pallets Belgium, warehouse pallets');
    setMeta('meta[name="robots"]', 'name', 'index, follow');

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };
    setOg('og:title', 'Grade B Used Euro Pallets Antwerp | Budget Pallets Belgium | PEL Partner');
    setOg('og:description', 'Cost-effective Grade B used Euro pallets in Antwerp, Belgium. Quality inspected, structurally sound, great value. Prices from €6/pallet.');
    setOg('og:url', `${siteUrl}/pallets/grade-b`);
    setOg('og:type', 'product');
    setOg('og:image', 'https://public.readdy.ai/ai/img_res/6203e656-81f6-4e73-888e-80c3b1d5925f.png');

    const setTwitter = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };
    setTwitter('twitter:card', 'summary_large_image');
    setTwitter('twitter:title', 'Grade B Used Euro Pallets Antwerp | Budget Pallets Belgium');
    setTwitter('twitter:description', 'Cost-effective Grade B used Euro pallets in Antwerp, Belgium. Quality inspected, structurally sound, great value. Prices from €6/pallet.');
    setTwitter('twitter:image', 'https://public.readdy.ai/ai/img_res/6203e656-81f6-4e73-888e-80c3b1d5925f.png');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) { canonical.setAttribute('href', `${siteUrl}/pallets/grade-b`); }
    else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `${siteUrl}/pallets/grade-b`);
      document.head.appendChild(canonical);
    }

    // Schema.org JSON-LD
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Grade B Used Euro Pallets',
      description: 'Cost-effective Grade B used Euro pallets. Quality inspected, structurally sound, ideal for standard warehousing and logistics. Available from Antwerp, Belgium.',
      image: 'https://public.readdy.ai/ai/img_res/6203e656-81f6-4e73-888e-80c3b1d5925f.png',
      url: `${siteUrl}/pallets/grade-b`,
      brand: { '@type': 'Brand', name: 'PEL Partner' },
      category: 'Industrial Pallets',
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'Dimensions', value: '1200 × 800 mm' },
        { '@type': 'PropertyValue', name: 'Load Capacity', value: 'Up to 1000 kg' },
        { '@type': 'PropertyValue', name: 'Condition', value: 'Good — minor cosmetic wear' },
      ],
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'EUR',
        lowPrice: '8',
        highPrice: '12',
        offerCount: '4',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: 'PEL Partner', url: siteUrl },
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Products', item: `${siteUrl}/#used-pallets` },
        { '@type': 'ListItem', position: 3, name: 'Grade B Pallets', item: `${siteUrl}/pallets/grade-b` },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Grade B Used Euro Pallets – PEL Partner Antwerp',
      description: 'Cost-effective Grade B used Euro pallets from Antwerp, Belgium. Prices from €6/pallet.',
      url: `${siteUrl}/pallets/grade-b`,
      inLanguage: 'en',
      isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'PEL Partner' },
      breadcrumb: breadcrumbSchema,
    };

    const schemas = [productSchema, breadcrumbSchema, webPageSchema];
    const scriptEls: HTMLScriptElement[] = [];
    schemas.forEach((schema, i) => {
      const existing = document.getElementById(`schema-grade-b-${i}`);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      script.id = `schema-grade-b-${i}`;
      document.head.appendChild(script);
      scriptEls.push(script);
    });

    return () => { scriptEls.forEach((s) => { if (s.parentNode) s.parentNode.removeChild(s); }); };
  }, []);

  const getCurrentPricing = (qty: number) => {
    return pricingTiers.find(tier => {
      if (tier.range.includes('+')) {
        const minQty = parseInt(tier.range.replace('+', ''));
        return qty >= minQty;
      }
      const [min, max] = tier.range.split('-').map(n => parseInt(n));
      return qty >= min && qty <= max;
    }) || pricingTiers[0];
  };

  const currentPricing = getCurrentPricing(quantity);
  const totalPrice = currentPricing.price * quantity;

  const scrollToBanking = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const navLinks = [
    { labelKey: 'nav_used_pallets', href: '/#used-pallets' },
    { labelKey: 'nav_new_pallets', href: '/#new-pallets' },
    { labelKey: 'nav_services', href: '/#services' },
    { labelKey: 'nav_about', href: '/#about' },
    { labelKey: 'nav_faq', href: '/#faq' },
    { labelKey: 'nav_contact', href: '/#contact' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-gray-800 font-sans">
      {/* ── NAVBAR ── */}
      <nav className={`fixed w-full z-50 transition-all duration-400 ${scrolled ? 'bg-white/98 backdrop-blur-md shadow-lg' : 'bg-white/98 backdrop-blur-md shadow-lg'}`}>
        <div className="px-6 lg:px-16 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 cursor-pointer">
            <div className="h-20 w-20 flex items-center justify-center">
              <img src={LOGO_URL} alt="PEL Partner Logo" className="h-20 w-20 object-contain drop-shadow-lg" />
            </div>
            <span className="text-3xl font-bold tracking-widest uppercase text-gray-900">PEL Partner</span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(item => (
              <a key={item.labelKey} href={item.href} className="text-sm tracking-widest uppercase transition-colors whitespace-nowrap cursor-pointer font-medium text-gray-600 hover:text-gray-900">{t(item.labelKey)}</a>
            ))}
            <a href="/#contact" className="px-5 py-2 bg-yellow-500 text-black text-sm font-bold rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer tracking-wider uppercase shadow-md">{t('nav_get_quote')}</a>
          </div>
          <button className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer" onClick={() => setMenuOpen(!menuOpen)}>
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl text-gray-900`}></i>
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 px-6 pb-6 flex flex-col gap-4">
            {navLinks.map(item => (
              <a key={item.labelKey} href={item.href} onClick={() => setMenuOpen(false)} className="text-gray-700 hover:text-gray-900 uppercase tracking-widest text-sm cursor-pointer font-medium">{t(item.labelKey)}</a>
            ))}
          </div>
        )}
      </nav>

      {/* ── BREADCRUMB ── */}
      <section className="pt-32 pb-6 px-6 lg:px-16 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <a href="/" className="hover:text-yellow-600 transition-colors cursor-pointer">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <a href="/#used-pallets" className="hover:text-yellow-600 transition-colors cursor-pointer">Products</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-bold">Grade B Pallets</span>
          </div>
        </div>
      </section>

      {/* ── HERO SECTION ── */}
      <section className="py-16 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: Image Gallery */}
            <div>
              <div className="relative rounded-xl overflow-hidden border border-gray-200 mb-4 shadow-lg" style={{ aspectRatio: '4/3' }}>
                <img src={productImages[activeImg]} alt="Grade B Euro Pallet" className="w-full h-full object-cover object-top transition-all duration-300" />
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase bg-amber-700 text-yellow-100 shadow-md">VALUE</span>
                {/* Watermark */}
                <div className="absolute bottom-4 right-3 flex items-center gap-1 bg-black/30 backdrop-blur-sm rounded-md px-2 py-1">
                  <img src={LOGO_URL} alt="PEL Partner" className="h-5 w-5 object-contain opacity-90" />
                  <span className="text-white text-[10px] font-black tracking-widest uppercase opacity-90">PEL Partner</span>
                </div>
              </div>
              <div className="mb-3">
                <p className="text-gray-500 text-xs tracking-widest uppercase mb-2 font-medium"><i className="ri-image-line mr-1"></i>Product Views</p>
                <div className="flex gap-3">
                  {productImages.map((img, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} className={`flex-1 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${activeImg === i ? 'border-amber-600 shadow-md' : 'border-gray-200 hover:border-amber-500'}`} style={{ aspectRatio: '4/3' }}>
                      <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-cover object-top" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Product Info */}
            <div>
              <div className="mb-2"><span className="text-amber-700 text-xs tracking-widest uppercase font-bold">Grade B</span></div>
              <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">Used Euro Pallets — Grade B</h1>
              <p className="text-gray-600 text-base leading-relaxed mb-6">Grade B pallets offer the best value for money for standard warehousing and logistics operations. These pallets may show minor cosmetic wear or small repairs, but are fully structurally sound and safe for use.</p>
              
              {/* Quality Badges */}
              <div className="flex items-center gap-2 mb-6 pb-6 border-b border-gray-200">
                {qualityBadges.map((badge, i) => (
                  <div key={i} className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
                    <i className={`${badge.icon} text-amber-700 text-sm`}></i>
                    <span className="text-xs font-bold text-gray-700">{badge.label}</span>
                  </div>
                ))}
              </div>

              {/* Pricing Tiers */}
              <div className="mb-6">
                <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-3">
                  <i className="ri-price-tag-3-line mr-2"></i>{t('calc_volume_pricing')}
                </h4>
                <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                  <div className="grid grid-cols-2 gap-3">
                    {pricingTiers.map((tier, i) => (
                      <div key={i} className={`flex items-center justify-between p-3 rounded-md border ${currentPricing.range === tier.range ? 'border-amber-600 bg-amber-100' : 'border-gray-200 bg-white'} transition-all`}>
                        <div>
                          <div className="text-gray-600 text-xs">{tier.range} {t('calc_pieces')}</div>
                          <div className={`font-bold text-sm ${currentPricing.range === tier.range ? 'text-amber-700' : 'text-gray-900'}`}>€{tier.price}/{t('calc_pallets')}</div>
                        </div>
                        {currentPricing.range === tier.range && (
                          <i className="ri-check-line text-amber-700"></i>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quantity Selector & Payment */}
              <div className="mb-6 bg-gray-50 rounded-lg border border-gray-200 p-4">
                <h4 className="text-gray-900 text-xs tracking-widest uppercase font-black mb-3">
                  <i className="ri-shopping-cart-line mr-2"></i>{t('calc_select_qty')}
                </h4>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center border border-gray-300 rounded-md bg-white">
                    <button 
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <input 
                      type="number" 
                      min="1" 
                      value={quantity} 
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20 px-3 py-2 text-center border-l border-r border-gray-300 focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-600/20 transition-all"
                    />
                    <button 
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <div className="flex-1">
                    <div className="text-gray-500 text-xs">{t('calc_current_price')}</div>
                    <div className="text-gray-900 font-bold text-lg">€{totalPrice.toFixed(2)}</div>
                    <div className="text-gray-500 text-xs">({quantity} × €{currentPricing.price}) — {t('calc_volume_hint')}</div>
                  </div>
                </div>
                
                {/* Payment Methods */}
                <div className="mt-4">
                  <div className="text-gray-700 text-xs font-bold mb-2">{t('calc_payment_methods_label')}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-amber-600 hover:bg-amber-50 transition-all cursor-pointer">
                      <i className="ri-bank-card-line text-blue-600"></i>
                      <span className="text-xs font-bold text-gray-700">Card Payment</span>
                    </button>
                    <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-amber-600 hover:bg-amber-50 transition-all cursor-pointer">
                      <i className="ri-google-fill text-red-500"></i>
                      <span className="text-xs font-bold text-gray-700">Google Pay</span>
                    </button>
                    <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-amber-600 hover:bg-amber-50 transition-all cursor-pointer">
                      <i className="ri-paypal-fill text-blue-700"></i>
                      <span className="text-xs font-bold text-gray-700">PayPal</span>
                    </button>
                    <button onClick={scrollToBanking} className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-amber-600 hover:bg-amber-50 transition-all cursor-pointer">
                      <i className="ri-bank-line text-purple-600"></i>
                      <span className="text-xs font-bold text-gray-700">Bancontact</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={scrollToContact} className="flex-1 text-center px-6 py-3 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md">
                  <i className="ri-shopping-cart-line mr-2"></i>{t('product_order_now')} €{totalPrice.toFixed(2)}
                </button>
                <button onClick={scrollToContact} className="flex-1 text-center px-6 py-3 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-amber-600 hover:bg-amber-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase">{t('product_request_quote')}</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TECHNICAL SPECS ── */}
      <section className="py-16 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">{t('product_tech_specs')}</h2>
            <div className="w-16 h-1 bg-amber-600 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {technicalSpecs.map((spec, i) => (
              <div key={i} className="bg-white rounded-lg px-4 py-4 border border-gray-200 hover:border-amber-500 transition-all">
                <div className="text-gray-500 text-xs mb-1 tracking-widest uppercase">{spec.label}</div>
                <div className="text-gray-900 text-sm font-bold">{spec.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── KEY FEATURES ── */}
      <section className="py-16 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">{t('product_key_features')}</h2>
            <div className="w-16 h-1 bg-amber-600 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {keyFeatures.map((feature, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg p-4 hover:border-amber-500 transition-all">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-full border border-amber-200 shrink-0">
                  <i className="ri-checkbox-circle-fill text-amber-600"></i>
                </div>
                <span className="text-gray-900 font-bold text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RELATED PRODUCTS ── */}
      <section className="py-16 px-6 lg:px-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">{t('product_view_details')}</h2>
            <div className="w-16 h-1 bg-amber-600 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <a href="/pallets/grade-a" className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-yellow-400 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer">
              <div className="relative h-48 w-full overflow-hidden">
                <img src="https://static.readdy.ai/image/aab8598c00afda4d0b512811ae1e1dec/9b340ad5763236b594c0cf9fc081388b.jpeg" alt="Grade A Pallets" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase bg-yellow-500 text-black shadow-md">PREMIUM</span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-black text-gray-900 mb-2">Grade A Used Euro Pallets</h3>
                <p className="text-gray-600 text-sm mb-4">Premium quality with minimal wear. EPAL certified.</p>
                <div className="text-amber-700 font-bold text-sm">From €7.5/pallet <i className="ri-arrow-right-line ml-1"></i></div>
              </div>
            </a>
            <a href="/pallets/new" className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-green-600 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl cursor-pointer">
              <div className="relative h-48 w-full overflow-hidden">
                <img src="https://public.readdy.ai/ai/img_res/a0b5b946-f08e-472c-9146-e6f01453ec3f.png" alt="New Pallets" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase bg-green-700 text-yellow-100 shadow-md">NEW</span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-black text-gray-900 mb-2">New Wooden Pallets</h3>
                <p className="text-gray-600 text-sm mb-4">Brand new, ISPM15 certified, export ready.</p>
                <div className="text-green-700 font-bold text-sm">From €10/pallet <i className="ri-arrow-right-line ml-1"></i></div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ── CONTACT SECTION ── */}
      <section id="contact" className="py-20 px-6 lg:px-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">{t('contact_title')}</h2>
            <div className="w-16 h-1 bg-amber-600 mx-auto mb-6"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('contact_info_title')}</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-black text-gray-900 mb-6">{t('contact_info_title')}</h3>
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-lg border border-amber-200 shrink-0">
                    <i className="ri-phone-line text-amber-700 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs tracking-widest uppercase mb-1">{t('contact_phone_label')}</div>
                    <a href="tel:+32487261970" className="text-gray-900 font-bold hover:text-amber-700 transition-colors cursor-pointer">+32 487 26 19 70</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-lg border border-amber-200 shrink-0">
                    <i className="ri-mail-line text-amber-700 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs tracking-widest uppercase mb-1">{t('contact_email_label')}</div>
                    <a href="mailto:pelpartnercompany@gmail.com" className="text-gray-900 font-bold hover:text-amber-700 transition-colors cursor-pointer break-all">pelpartnercompany@gmail.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-lg border border-amber-200 shrink-0">
                    <i className="ri-map-pin-2-line text-amber-700 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-gray-500 text-xs tracking-widest uppercase mb-1">{t('contact_location_label')}</div>
                    <div className="text-gray-900 font-bold">{t('about_location')}</div>
                  </div>
                </div>
              </div>

              {/* Banking Information */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
                <h4 className="text-gray-900 font-black text-lg mb-4 flex items-center gap-2">
                  <i className="ri-bank-card-line text-amber-700"></i>
                  Banking Information
                </h4>
                
                {/* EUR Account */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full">
                      <span className="text-blue-600 text-xs font-black">€</span>
                    </div>
                    <span className="text-gray-900 font-black text-base">Euro Account</span>
                  </div>
                  <div className="space-y-3 pl-10">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Beneficiary Name</div>
                      <div className="text-gray-900 font-bold">Darav Khalid</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Bank Name</div>
                      <div className="text-gray-900 font-bold">Revolut Bank UAB</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Bank Address</div>
                      <div className="text-gray-900 font-bold">Konstitucijos ave. 21B, 08130, Vilnius, Lithuania</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">EUR IBAN</div>
                      <div className="text-gray-900 font-bold font-mono text-sm bg-white px-3 py-2 rounded border border-gray-200">LT71 3250 0690 5068 8936</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">BIC/SWIFT Code</div>
                      <div className="text-gray-900 font-bold font-mono text-sm bg-white px-3 py-2 rounded border border-gray-200">REVOLT21</div>
                    </div>
                  </div>
                </div>

                {/* GBP Account */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-full">
                      <span className="text-purple-600 text-xs font-black">£</span>
                    </div>
                    <span className="text-gray-900 font-black text-base">British Pound Account</span>
                  </div>
                  <div className="space-y-3 pl-10">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Beneficiary Name</div>
                      <div className="text-gray-900 font-bold">Darav Khalid</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Bank Name</div>
                      <div className="text-gray-900 font-bold">Revolut Ltd</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Bank Address</div>
                      <div className="text-gray-900 font-bold">30 South Colonnade, E14 5HX, London, United Kingdom</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Sort Code</div>
                      <div className="text-gray-900 font-bold font-mono text-sm bg-white px-3 py-2 rounded border border-gray-200">23-01-20</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Account Number</div>
                      <div className="text-gray-900 font-bold font-mono text-sm bg-white px-3 py-2 rounded border border-gray-200">87899767</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl border border-gray-200 p-8">
              <h3 className="text-2xl font-black text-gray-900 mb-6">Quick Contact</h3>
              <div className="space-y-4">
                <a href="tel:+32487261970" className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-yellow-500 text-black font-black rounded-md hover:bg-yellow-400 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md">
                  <i className="ri-phone-line"></i>
                  {t('contact_call_now')}
                </a>
                <a href="mailto:pelpartnercompany@gmail.com" className="w-full flex items-center justify-center gap-2 px-6 py-4 border-2 border-gray-300 text-gray-700 font-black rounded-md hover:border-amber-600 hover:bg-amber-50 transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase">
                  <i className="ri-mail-line"></i>
                  {t('contact_email_us')}
                </a>
                <a href="https://wa.me/32487261970" target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#25D366] text-white font-black rounded-md hover:bg-[#20BA5A] transition-all whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase shadow-md">
                  <i className="ri-whatsapp-line"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#2C2C2C] border-t border-gray-700 py-14 px-6 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="PEL Partner Logo" className="h-10 w-10 object-contain" />
                <span className="text-xl font-black tracking-widest text-white uppercase">PEL Partner</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed max-w-xs">{t('footer_desc')}</p>
            </div>
            <div>
              <h4 className="text-white font-black text-xs tracking-widest uppercase mb-5">{t('footer_products')}</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/pallets/grade-a" className="hover:text-yellow-400 transition-colors cursor-pointer">{t('form_product_a')}</a></li>
                <li><a href="/pallets/grade-b" className="hover:text-yellow-400 transition-colors cursor-pointer">{t('form_product_b')}</a></li>
                <li><a href="/pallets/new" className="hover:text-yellow-400 transition-colors cursor-pointer">{t('form_product_new')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-black text-xs tracking-widest uppercase mb-5">{t('footer_contact')}</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex items-center gap-2"><i className="ri-map-pin-line text-yellow-500"></i> {t('about_location')}</li>
                <li><a href="tel:+32487261970" className="flex items-center gap-2 hover:text-yellow-400 transition-colors cursor-pointer"><i className="ri-phone-line text-yellow-500"></i> +32 487 26 19 70</a></li>
                <li><a href="mailto:pelpartnercompany@gmail.com" className="flex items-center gap-2 hover:text-yellow-400 transition-colors cursor-pointer break-all"><i className="ri-mail-line text-yellow-500"></i> pelpartnercompany@gmail.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-xs">{t('footer_copyright')}</p>
            <p className="text-gray-500 text-xs">{t('footer_tagline')}</p>
          </div>
        </div>
      </footer>

      {/* ── WHATSAPP BUTTON ── */}
      <a href="https://wa.me/32487261970" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-[#25D366] text-white rounded-full shadow-2xl hover:scale-110 transition-transform cursor-pointer" aria-label="Contact us on WhatsApp">
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>
    </div>
  );
}